import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Mic, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/assistant/transcribe`;
const MAX_MS = 20000;
const pickType = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"]
  .find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";

export function MicButton({ lang, t, onText, onError, disabled }) {
  const [state, setState] = useState("idle");
  const rec = useRef(null);
  const timer = useRef(null);

  useEffect(() => () => { clearTimeout(timer.current); rec.current?.stream?.getTracks().forEach((x) => x.stop()); }, []);

  if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return null;

  const send = async (blob) => {
    setState("busy");
    const fd = new FormData();
    fd.append("audio", blob, `q.${blob.type.includes("mp4") ? "mp4" : blob.type.includes("mpeg") ? "mp3" : "webm"}`);
    fd.append("lang", lang);
    try {
      const { data } = await axios.post(API, fd, { timeout: 45000 });
      onText(data.text);
    } catch (e) {
      onError(t(e.response?.status === 422 ? "I didn't catch that. Try again a little closer to the phone." : "Couldn't hear the recording. Try again or type your question."));
    } finally { setState("idle"); }
  };

  const start = async () => {
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch {
      onError(t("Microphone is blocked. Allow it in your browser settings to talk to the helper.")); return;
    }
    const type = pickType();
    let r;
    try { r = new MediaRecorder(stream, type ? { mimeType: type } : undefined); } catch {
      stream.getTracks().forEach((x) => x.stop());
      onError(t("Couldn't hear the recording. Try again or type your question.")); return;
    }
    const chunks = [];
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = () => {
      stream.getTracks().forEach((x) => x.stop());
      const blob = new Blob(chunks, { type: (r.mimeType || type || "audio/webm").split(";")[0] });
      if (blob.size > 1200) send(blob); else setState("idle");
    };
    rec.current = r;
    r.start();
    setState("rec");
    timer.current = setTimeout(stop, MAX_MS);
  };

  const stop = () => {
    clearTimeout(timer.current);
    if (rec.current?.state === "recording") rec.current.stop();
  };

  return (
    <button type="button" data-testid="help-mic" disabled={disabled || state === "busy"}
      className={`mic-btn ${state === "rec" ? "mic-rec" : ""}`}
      aria-label={state === "rec" ? t("Stop and send") : t("Ask by voice")}
      onClick={state === "rec" ? stop : start}>
      {state === "busy" ? <Loader2 size={20} className="spin" /> : state === "rec" ? <Square size={18} /> : <Mic size={20} />}
    </button>
  );
}
